import { z } from 'zod';
import { uuidSchema } from './common.js';
/**
 * Supplier Register (D-059/D-060/D-100, RN-18 §4.1) — the governed golden record per legal entity.
 * Lean first cut: identity + external IDs (dedup keys) + country + status lifecycle. Bank details and
 * payment terms (D-102 dual control), addresses/contacts, and the fuzzy match-merge engine come later.
 * Shapes shared by the API (validation) and the web app (types).
 */
export const supplierStatusSchema = z.enum(['prospect', 'onboarding', 'active', 'blocked', 'archived']);
/** Bank-info validation state (D-141). The latest bank values always show; this labels them. */
export const bankValidationStatusSchema = z.enum(['validated', 'not_validated']);
export const supplierSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    legalName: z.string().nullable(),
    country: z.string().nullable(),
    vatNumber: z.string().nullable(),
    lei: z.string().nullable(),
    duns: z.string().nullable(),
    address: z.string().nullable(),
    contactName: z.string().nullable(),
    contactEmail: z.string().nullable(),
    contactPhone: z.string().nullable(),
    iban: z.string().nullable(),
    bic: z.string().nullable(),
    bankName: z.string().nullable(),
    accountHolder: z.string().nullable(),
    paymentTerms: z.string().nullable(),
    // Bank-info validation (D-141): status + who validates + last change, shown next to the bank values.
    bankValidationStatus: bankValidationStatusSchema,
    bankFinanceEmail: z.string().nullable(),
    bankChangedAt: z.string().nullable(),
    bankChangedBy: z.string().nullable(),
    bankValidatedAt: z.string().nullable(),
    status: supplierStatusSchema,
    source: z.enum(['manual', 'ai_seeded', 'portal']),
    isDemo: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** POST /master-data/suppliers — add a supplier (source becomes 'manual'). */
export const createSupplierSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    legalName: z.string().trim().max(200).optional(),
    country: z.string().trim().max(2).optional(), // ISO 3166 alpha-2
    vatNumber: z.string().trim().max(32).optional(),
    lei: z.string().trim().max(20).optional(),
    duns: z.string().trim().max(16).optional(),
    address: z.string().trim().max(400).optional(),
    contactName: z.string().trim().max(120).optional(),
    contactEmail: z.string().trim().max(200).optional(),
    contactPhone: z.string().trim().max(40).optional(),
    status: supplierStatusSchema.optional(),
})
    .strict();
/**
 * PATCH /master-data/suppliers/:id — edit identity / IDs / status, and (D-141) the buyer-editable bank
 * fields. Payment terms are buyer-only (never proposed by the supplier). A change to the bank values
 * (iban/bic/bankName/accountHolder) flips the validation status to "not validated" and emails the
 * supplier's finance colleague a validation link; changing payment terms alone does not. financeEmail
 * appoints (or re-points) the finance colleague who validates.
 */
export const patchSupplierSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    legalName: z.string().trim().max(200).nullable().optional(),
    country: z.string().trim().max(2).nullable().optional(),
    vatNumber: z.string().trim().max(32).nullable().optional(),
    lei: z.string().trim().max(20).nullable().optional(),
    duns: z.string().trim().max(16).nullable().optional(),
    address: z.string().trim().max(400).nullable().optional(),
    contactName: z.string().trim().max(120).nullable().optional(),
    contactEmail: z.string().trim().max(200).nullable().optional(),
    contactPhone: z.string().trim().max(40).nullable().optional(),
    iban: z.string().trim().max(40).nullable().optional(),
    bic: z.string().trim().max(20).nullable().optional(),
    bankName: z.string().trim().max(160).nullable().optional(),
    accountHolder: z.string().trim().max(200).nullable().optional(),
    paymentTerms: z.string().trim().max(120).nullable().optional(),
    financeEmail: z.string().trim().email().max(200).nullable().optional(),
    status: supplierStatusSchema.optional(),
})
    .strict();
/**
 * POST /master-data/suppliers/import — bulk import (BP-21/D-126). The client parses the CSV/Excel and
 * sends the rows as objects keyed by field name; the server picks the known fields, re-validates each
 * against the create schema, skips VAT/LEI duplicates, inserts the rest, and returns a report.
 */
export const importSuppliersRequestSchema = z
    .object({ rows: z.array(z.record(z.unknown())).min(1).max(5000) })
    .strict();
/**
 * GET /master-data/suppliers/lookup — free external enrichment (D-137). Given an LEI (GLEIF) or an EU
 * VAT number + country (VIES), the server fetches the official legal name + registered address so the
 * user can confirm-then-save. No DB write: it returns a suggestion the caller applies on the form.
 */
export const supplierLookupQuerySchema = z
    .object({
    lei: z.string().trim().max(20).optional(),
    vatNumber: z.string().trim().max(32).optional(),
    country: z.string().trim().max(2).optional(),
})
    .strict();
export const supplierLookupResultSchema = z.object({
    source: z.enum(['gleif', 'vies', 'none']),
    matched: z.boolean(),
    legalName: z.string().nullable(),
    address: z.string().nullable(),
    country: z.string().nullable(),
    note: z.string().nullable(),
});
export const importSuppliersResultSchema = z.object({
    inserted: z.number(),
    skipped: z.array(z.object({ row: z.number(), name: z.string().optional(), reason: z.string() })),
    errors: z.array(z.object({ row: z.number(), reason: z.string() })),
});
//# sourceMappingURL=suppliers.js.map